import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  AccessControl,
  Button,
  DataTableWithoutCheckbox,
  FieldSelect,
  FieldTextInput,
  hasAccess,
  Modal,
  PERMISSION_KEYS,
  PERMISSION_TYPES,
  ToastBar,
  ValidationError,
} from '../../components';
import { EnterViceService } from '../../services';
import type { Paged, PresetListItem, PresetStatus } from '../../types/entervice';
import { dateFormatting } from '../../utils/dateMethods';
import editIcon from '../../assets/svg/edit.svg';
import duplicateIcon from '../../assets/svg/add.svg';
import activateIcon from '../../assets/svg/unblock.svg';
import deactivateIcon from '../../assets/svg/block.svg';
import deleteIcon from '../../assets/svg/delete.svg';
import StatusChip from './parts/StatusChip';
import ConfirmDialog from './parts/ConfirmDialog';
import RankEditor from './parts/RankEditor';
import { describeError } from './parts/errors';
import '../LayoutWrapperMain/LayoutWrapperMain.module.scss';
import './entervice.scss';

type DialogKind = 'activate' | 'deactivate' | 'duplicate' | 'delete';
type DialogState = { kind: 'none' } | { kind: DialogKind; preset: PresetListItem };

interface FilterValues {
  search: string;
  status: string;
}

interface ListFilter {
  page: number;
  rowsPerPage: number;
  status: PresetStatus | '';
  search: string;
}

const STATUS_OPTIONS = [
  { key: 'all', label: 'All statuses' },
  { key: 'draft', label: 'Draft' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

const DIALOG_COPY: Record<DialogKind, { title: string; confirm: string; danger?: boolean; message: (p: PresetListItem) => string }> = {
  activate: {
    title: 'Activate preset',
    confirm: 'Activate',
    message: (p) => `Publish “${p.name}” to the app? Users see it in the preset list right away.`,
  },
  deactivate: {
    title: 'Deactivate preset',
    confirm: 'Deactivate',
    message: (p) => `Hide “${p.name}” from the app? Generations already made keep working.`,
  },
  duplicate: {
    title: 'Duplicate preset',
    confirm: 'Duplicate',
    message: (p) => `Create a draft copy of “${p.name}”? The copy gets its own key and starts without test runs.`,
  },
  delete: {
    title: 'Delete preset',
    confirm: 'Delete',
    danger: true,
    message: (p) => `Delete “${p.name}”? It disappears from the panel and the app.`,
  },
};

const EnterVicePresets: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ListFilter>({
    page: 0, rowsPerPage: 10, status: '', search: '',
  });
  const [list, setList] = useState<Paged<PresetListItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  const [showRanks, setShowRanks] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const filterForm = useForm<FilterValues>({ defaultValues: { search: '', status: 'all' } });

  const canAdd = hasAccess(PERMISSION_KEYS.ENTER_VICE, PERMISSION_TYPES.ADD);
  const canEdit = hasAccess(PERMISSION_KEYS.ENTER_VICE, PERMISSION_TYPES.EDIT);
  const canDelete = hasAccess(PERMISSION_KEYS.ENTER_VICE, PERMISSION_TYPES.DELETE);
  const showActions = canAdd || canEdit || canDelete;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    EnterViceService.listPresets({
      page: filter.page + 1,
      limit: filter.rowsPerPage,
      status: filter.status || undefined,
      search: filter.search || undefined,
    })
      .then((page) => {
        if (!cancelled) setList(page);
      })
      .catch((e) => {
        if (!cancelled) setError(describeError(e, 'Failed to load presets.').message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter, reloadKey]);

  const reload = () => setReloadKey((key) => key + 1);

  const onFilter = (values: FilterValues) => {
    setFilter({
      ...filter,
      page: 0,
      search: values.search.trim(),
      status: values.status === 'all' ? '' : (values.status as PresetStatus),
    });
  };

  const onResetFilter = () => {
    filterForm.reset({ search: '', status: 'all' });
    setFilter({ ...filter, page: 0, search: '', status: '' });
  };

  const closeDialog = () => setDialog({ kind: 'none' });

  const runDialogAction = async () => {
    if (dialog.kind === 'none') return;
    const { preset } = dialog;
    switch (dialog.kind) {
      case 'activate':
        await EnterViceService.setPresetStatus(preset._id, 'active');
        setToast(`“${preset.name}” is now active.`);
        break;
      case 'deactivate':
        await EnterViceService.setPresetStatus(preset._id, 'inactive');
        setToast(`“${preset.name}” deactivated.`);
        break;
      case 'duplicate': {
        const copy = await EnterViceService.duplicatePreset(preset._id);
        setToast(`Duplicated as “${copy.name}” (draft).`);
        break;
      }
      case 'delete':
        await EnterViceService.deletePreset(preset._id);
        setToast(`“${preset.name}” deleted.`);
        break;
      default:
        break;
    }
    closeDialog();
    reload();
  };

  const renderActions = (preset: PresetListItem) => (
    <div className="ev-actions">
      <AccessControl permissionKey={PERMISSION_KEYS.ENTER_VICE} accessType={PERMISSION_TYPES.EDIT}>
        <img
          src={editIcon}
          role="presentation"
          alt="edit"
          title="Edit"
          onClick={() => navigate(`/entervice/editPreset/${preset._id}`)}
        />
      </AccessControl>
      <AccessControl permissionKey={PERMISSION_KEYS.ENTER_VICE} accessType={PERMISSION_TYPES.ADD}>
        <img
          src={duplicateIcon}
          role="presentation"
          alt="duplicate"
          title="Duplicate as draft"
          onClick={() => setDialog({ kind: 'duplicate', preset })}
        />
      </AccessControl>
      <AccessControl permissionKey={PERMISSION_KEYS.ENTER_VICE} accessType={PERMISSION_TYPES.EDIT}>
        {preset.status === 'active' ? (
          <img
            src={deactivateIcon}
            role="presentation"
            alt="deactivate"
            title="Deactivate"
            onClick={() => setDialog({ kind: 'deactivate', preset })}
          />
        ) : (
          <img
            src={activateIcon}
            role="presentation"
            alt="activate"
            title="Activate"
            onClick={() => setDialog({ kind: 'activate', preset })}
          />
        )}
      </AccessControl>
      <AccessControl permissionKey={PERMISSION_KEYS.ENTER_VICE} accessType={PERMISSION_TYPES.DELETE}>
        <img
          src={deleteIcon}
          role="presentation"
          alt="delete"
          title="Delete"
          onClick={() => setDialog({ kind: 'delete', preset })}
        />
      </AccessControl>
    </div>
  );

  const testsCell = (preset: PresetListItem) => {
    if (!preset.lastTestAt) return 'none yet';
    const video = preset.hasVideo ? ` · ${preset.successfulVideoTests || 0} video` : '';
    return `${preset.successfulTests} ok${video} · ${dateFormatting(preset.lastTestAt)}`;
  };

  // WHY: DataTableWithoutCheckbox renders row keys in insertion order (skipping `id`), so this order is the column order.
  const rows = (list ? list.data : []).map((preset) => ({
    id: preset._id,
    thumb: preset.thumbnailUrl
      ? <img className="ev-thumb" src={preset.thumbnailUrl} alt={preset.name} />
      : <div className="ev-thumb ev-thumb--empty">none</div>,
    name: (
      <div className="ev-cell-name">
        <span className="ev-name">{preset.name}</span>
        <span className="ev-key">{preset.key} · v{preset.version}</span>
      </div>
    ),
    status: <StatusChip status={preset.status} />,
    rank: preset.rank ?? '—',
    model: <span className="ev-mono">{preset.model}{preset.hasVideo ? ' + video' : ''}</span>,
    tests: testsCell(preset),
    updatedAt: dateFormatting(preset.updatedAt),
    ...(showActions ? { action: renderActions(preset) } : {}),
  }));

  const headCells = [
    { id: 'thumb', label: 'Thumbnail', sort: false },
    { id: 'name', label: 'Preset', sort: false },
    { id: 'status', label: 'Status', sort: false },
    { id: 'rank', label: 'Rank', sort: false },
    { id: 'model', label: 'Model', sort: false },
    { id: 'tests', label: 'Tests', sort: false },
    { id: 'updatedAt', label: 'Updated', sort: false },
    ...(showActions ? [{ id: 'action', label: 'Action', sort: false }] : []),
  ];

  return (
    <>
      <Modal
        id="entervice-dialog"
        isOpen={dialog.kind !== 'none' || showRanks}
        onClose={() => { closeDialog(); setShowRanks(false); }}
        onManageDisableScrolling={() => null}
        lightCloseButton={false}
      >
        {showRanks && (
          <RankEditor
            onClose={() => setShowRanks(false)}
            onSaved={() => {
              setShowRanks(false);
              setToast('Preset order saved.');
              reload();
            }}
          />
        )}
        {dialog.kind !== 'none' && (
          <ConfirmDialog
            title={DIALOG_COPY[dialog.kind].title}
            message={DIALOG_COPY[dialog.kind].message(dialog.preset)}
            confirmLabel={DIALOG_COPY[dialog.kind].confirm}
            danger={DIALOG_COPY[dialog.kind].danger}
            onConfirm={runDialogAction}
            onClose={closeDialog}
          />
        )}
      </Modal>

      {toast && <ToastBar message={toast} handleCloseMethod={() => setToast('')} />}

      <div>
        <div className="topButton">
          <AccessControl permissionKey={PERMISSION_KEYS.ENTER_VICE} accessType={PERMISSION_TYPES.EDIT}>
            <Button buttonType="button" onClick={() => setShowRanks(true)}>Edit Ranks</Button>
          </AccessControl>
          <AccessControl permissionKey={PERMISSION_KEYS.ENTER_VICE} accessType={PERMISSION_TYPES.ADD}>
            <Button buttonType="button" onClick={() => navigate('/entervice/addPreset')}>+ New Preset</Button>
          </AccessControl>
        </div>

        <form className="cardBg ev-filter" onSubmit={filterForm.handleSubmit(onFilter)}>
          <FieldTextInput
            id="search"
            name="search"
            formName="enterviceFilter"
            className="textInput"
            type="text"
            formProps={filterForm}
            label="Search by name or key"
          />
          <FieldSelect
            id="status"
            name="status"
            label="Status"
            options={STATUS_OPTIONS}
            formProps={filterForm}
            defaultValue="all"
          />
          <div className="ev-filter-actions">
            <Button buttonType="submit">Search</Button>
            <Button buttonType="button" className="ev-btn-ghost" onClick={onResetFilter}>Reset</Button>
          </div>
        </form>

        {error && <ValidationError className="validation_error" error={error} />}

        <div className="dataTableCover">
          <DataTableWithoutCheckbox
            headCells_={headCells}
            rows_={rows}
            rowClick={() => null}
            selected={[]}
            setSelected={() => null}
            setSelectedRows={() => null}
            page={filter.page}
            rowsPerPage={filter.rowsPerPage}
            loading={loading}
            handleChangePage={(_event: unknown, newPage: number) => setFilter({ ...filter, page: newPage })}
            handleChangeRowsPerPage={(event: React.ChangeEvent<HTMLInputElement>) => setFilter({
              ...filter, page: 0, rowsPerPage: parseInt(event.target.value, 10),
            })}
            handleSort={() => null}
            count={list ? list.total : 0}
          />
        </div>
      </div>
    </>
  );
};

export default EnterVicePresets;
